<template lang="pug">
.waiter-detail-tab(v-cloak)
  .mb-3
  .template-container.ml-2(v-if="waiterLoaded")
    .d-flex.flex-row.flex-wrap-reverse
      .item-content.d-flex.flex-column
        .information.mb-2(v-if="waiter.waiter.admissionApplication.information" style="white-space: pre-wrap; word-wrap: break-word;") 
          span {{ waiter.waiter.admissionApplication.information }}

        .template(v-for="(property, index) in waiter.waiter.admissionApplication.templateAnswer.properties")
          WaiterTemplateItem(v-model="waiter.waiter.admissionApplication.templateAnswer.properties[index]"
            :waiterViewableFacilitiesMap="waiterViewableFacilitiesMap" :isChaild="false")

        .created.mb-2
          .labels.d-flex.flex-row.align-items-center
            .label.mr-1 申込日
            span.validation-label.mr-1 必須
          .d-flex.flex-row.align-items-center
            span.mr-2 {{ convertYear(waiter.waiter.admissionApplication.created) }}
            span.mr-2 年
            span.mr-2 {{ convertMonth(waiter.waiter.admissionApplication.created) }}
            span.mr-2 月
            span.mr-2 {{ convertDate(waiter.waiter.admissionApplication.created) }}
            span.mr-2 日

      .buttons.d-flex.flex-row.justify-content-end.flex-fill
        .request-change
          button.btn.request-change-btn.mr-2(v-if="showChangeRequest" type="button" @click="onChangeRequest") 申込者からの変更申請
          .request-change-btn-badge(v-if="showChangeRequest")
            b-badge(pill variant="danger")
              .request-change-btn-badge-content
        button.btn.edit-btn(type="button" @click="gotoWaiterEdit") 修正

  RelativeLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";

import { userStore } from "@/store/store";
import LoginUser from "@/store/LoginUser";

import FacilitiesService, { FacilityGetModel } from "@/commons/services/Facilities";
import WaitersService, { WaitersGetModel } from "@/commons/services/Waiters";

import RelativeLoading from "@/pc/basics/RelativeLoading.vue";
import WaiterTemplateItem from "@/pc/containers/waiters/WaiterTemplateItem.vue";

/**
 * ### 待機者詳細タブコンポーネント
 */
@Component({
  components: {
    RelativeLoading,
    WaiterTemplateItem
  }
})
export default class WaiterDetailTab extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** 入所申込テンプレートサービス */
  @Inject("WaitersService") waitersService!: WaitersService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 待機者 */
  waiter: WaitersGetModel | null = null;
  /** 待機者情報が全て読み込まれたか否か */
  waiterLoaded: boolean = false;

  waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = new Map<number, FacilityGetModel>();

  get showChangeRequest(): boolean {
    if (this.waiter == null) {
      return false;
    }
    return this.waiter.waiter.applyingForChange;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const facilityId: number = Number(this.$route.params.facilityid);
    const residentId: number = Number(this.$route.params.residentid);
    const idinsystem: number = Number(this.$route.params.idinsystem);
    const waiter: WaitersGetModel = await this.getWaiter(facilityId, residentId, idinsystem);
    this.waiter = waiter;

    const waiterViewableFacilities: Array<FacilityGetModel> = await this.getWaiterViewableFacilities(userStore);
    const waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = this.createWaiterViewableFacilitiesMap(waiterViewableFacilities);
    this.waiterViewableFacilitiesMap = waiterViewableFacilitiesMap;

    this.waiterLoaded = true;

    this.isBusy = false;

    return;
  }

  /**
   * ### 閲覧可能施設を取得する
   */
  public async getWaiterViewableFacilities(userStore: LoginUser): Promise<Array<FacilityGetModel>> {
    const waiterViewableFacilities: Array<FacilityGetModel> = [];
    for (const facilityId of userStore.facility.waiterViewableFacilities) {
      const response: FacilityGetModel = await this.facilitiesService.get(userStore, { targetFacilityId: facilityId });
      waiterViewableFacilities.push(response);
    }
    return waiterViewableFacilities;
  }

  /**
   * ### 閲覧可能施設をMAP化する
   *
   * - key：施設ID、value：施設情報
   * - 自施設もMAPに含める
   */
  public createWaiterViewableFacilitiesMap(waiterViewableFacilities: Array<FacilityGetModel>): Map<number, FacilityGetModel> {
    let waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = new Map<number, FacilityGetModel>();

    waiterViewableFacilitiesMap.set(userStore.facility.id, userStore.facility);

    for (const facility of waiterViewableFacilities) {
      waiterViewableFacilitiesMap.set(facility.id, facility);
    }

    return waiterViewableFacilitiesMap;
  }

  /**
   * ### 待機者を取得する
   */
  public async getWaiter(facilityId: number, residentId: number, idinsystem: number): Promise<WaitersGetModel> {
    const waitersByIdInSystem: Array<WaitersGetModel> = await this.waitersService.getsByIdInSystem(userStore, idinsystem);
    for (const waiter of waitersByIdInSystem) {
      if (waiter.facilityId == facilityId) {
        return waiter;
      }
    }
    // 見つからない場合は個別取得する
    const waiter: WaitersGetModel = await this.waitersService.get(userStore, facilityId, residentId);
    return waiter;
  }

  public convertYear(date: number): string {
    if (!date) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(date, "yyyy");
  }

  public convertMonth(date: number): string {
    if (!date) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(date, "mm", false);
  }

  public convertDate(date: number): string {
    if (!date) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(date, "dd", false);
  }

  public onChangeRequest(): void {
    this.$emit("onChangeRequest");
    return;
  }

  /**
   * ### 修正ボタン押下を親コンポーネントの通知する
   */
  public gotoWaiterEdit(): void {
    this.$emit("onWaiterEdit");
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.waiter-detail-tab {
  position: relative;
  width: 100%;
  min-height: 240px;

  .item-content {
    max-width: 800px;
    min-width: 640px;

    .created {
      .label {
        font-weight: bold;
      }
      .validation-label {
        color: $validation-label-font-color;
        font-size: 10px;
        border: 1px solid $validation-label-font-color;
        padding: 1px 4px 0 4px;
        // 「必須」の文字が少し下にズレるでの、marginで調整する
        margin: 2px 6px 4px 0;
      }
    }
  }

  .buttons {
    width: 325px;

    .request-change {
      position: relative;
      .request-change-btn {
        width: 215px;
      }
      .request-change-btn-badge {
        position: absolute;
        top: -5px;
        left: 203px;
        z-index: 1;
        .request-change-btn-badge-content {
          width: 5px;
          height: 12px;
        }
      }
    }
    .edit-btn {
      width: 110px;
    }
  }
}
</style>
